import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from '../providers/AuthProvider';
import { TenantProvider } from '../providers/TenantProvider';
import { useAuth } from '../providers/AuthProvider';
import { LoginScreen } from '@/features/auth/components/LoginScreen';
import { AuthCallback } from '@/features/auth/components/AuthCallback';
import { DashboardLayout } from '@/features/dashboards/components/DashboardLayout';
import { DashboardHome } from '@/features/dashboards/components/DashboardHome';
import { SuperadminDashboard } from '@/features/dashboards/components/SuperadminDashboard';
import { ProfileLayout } from '@/features/profile/components/ProfileLayout';
import { ProfilePreferences } from '@/features/profile/components/ProfilePreferences';
import { ProfilePrivacy } from '@/features/profile/components/ProfilePrivacy';
import { LegalLayout } from '@/pages/legal/LegalLayout';
import { ContactsPage } from '@/features/crm/components/ContactsPage';
import { ContactDetailPage } from '@/features/crm/components/ContactDetailPage';
import { ImportContactsPage } from '@/features/crm/components/ImportContactsPage';
import { CampaignsPage } from '@/features/campaigns/components/CampaignsPage';
import { CampaignDetailPage } from '@/features/campaigns/components/CampaignDetailPage';
import { WarRoomPage } from '@/features/campaigns/components/WarRoomPage';
import { SettingsLayout } from '@/features/settings/components/SettingsLayout';
import { CategoriesSettings } from '@/features/settings/components/CategoriesSettings';
import { TagsSettings } from '@/features/settings/components/TagsSettings';
import { WhatsAppSettings } from '@/features/settings/components/WhatsAppSettings';
import { CopilotPage } from '@/features/copilot/components/CopilotPage';
import { EventsPage } from '@/features/events/components/EventsPage';
import { EventDetailPage } from '@/features/events/components/EventDetailPage';
import { FieldIntelligencePage } from '@/features/geo/components/FieldIntelligencePage';
import { AnalyticsPage } from '@/features/analytics/components/AnalyticsPage';

// Provedor que unifica os contextos globais
function AppLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

// Rota protegida: exige login ativo
function PrivateRoute() {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-midnight flex items-center justify-center">
        <div className="relative">
          <div className="absolute inset-0 rounded-full blur-2xl bg-accent/30" />
          <svg className="relative h-9 w-9 animate-spin text-accent" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Se autenticado, engloba no TenantProvider para RLS e Branding
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

// Rota exclusiva para superadmins — redireciona usuários comuns ao início
function SuperadminRoute() {
  const { isSuperAdmin, loading } = useAuth();

  if (loading) return null;
  if (!isSuperAdmin) return <Navigate to="/app" replace />;

  return <Outlet />;
}

// Rota pública para quem já está autenticado: redireciona ao painel interno
function PublicOnlyRoute() {
  const { session, loading } = useAuth();

  if (loading) return null;
  if (session) return <Navigate to="/" replace />;

  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      // Rotas públicas (apenas deslogados)
      {
        element: <PublicOnlyRoute />,
        children: [
          { path: '/login', element: <LoginScreen /> },
        ],
      },
      // Callback OAuth
      { path: '/auth/callback', element: <AuthCallback /> },

      // Rotas públicas gerais (LGPD / Termos)
      {
        path: '/legal',
        element: <LegalLayout />,
        children: [
          { path: 'privacidade', element: <div className="prose">Política de Privacidade...</div> },
          { path: 'termos', element: <div className="prose">Termos de Uso...</div> },
        ],
      },

      // Rotas Privadas protegidas por Login e Tenant
      {
        element: <PrivateRoute />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { path: '/', element: <Navigate to="/app" replace /> },
              {
                path: '/app',
                element: <DashboardHome />,
              },
              // Perfil do Usuário
              {
                path: '/perfil',
                element: <ProfileLayout />,
                children: [
                  { path: '', element: <Navigate to="conta" replace /> },
                  {
                    path: 'conta',
                    element: (
                      <div className="space-y-4">
                        <h2 className="text-xl font-bold text-primary">Minha Conta</h2>
                        <p className="text-text-muted text-sm">Gerencie seus dados pessoais básicos.</p>
                      </div>
                    ),
                  },
                  { path: 'preferencias', element: <ProfilePreferences /> },
                  { path: 'privacidade', element: <ProfilePrivacy /> },
                ],
              },
              // CRM — Contatos
              {
                path: '/app/contatos',
                element: <ContactsPage />,
              },
              {
                path: '/app/contatos/importar',
                element: <ImportContactsPage />,
              },
              {
                path: '/app/contatos/:id',
                element: <ContactDetailPage />,
              },
              // Campanhas & Disparos
              {
                path: '/app/campanhas',
                element: <CampaignsPage />,
              },
              {
                path: '/app/campanhas/:id',
                element: <CampaignDetailPage />,
              },
              {
                path: '/app/campanhas/:id/war-room',
                element: <WarRoomPage />,
              },
              // Copiloto IA
              {
                path: '/app/copiloto',
                element: <CopilotPage />,
              },
              // Agenda / Eventos
              {
                path: '/app/eventos',
                element: <EventsPage />,
              },
              {
                path: '/app/eventos/:id',
                element: <EventDetailPage />,
              },
              // Field Intelligence
              {
                path: '/app/campo',
                element: <FieldIntelligencePage />,
              },
              // Relatórios & Analytics
              {
                path: '/app/relatorios',
                element: <AnalyticsPage />,
              },
              // Configurações do tenant
              {
                path: '/app/configuracoes',
                element: <SettingsLayout />,
                children: [
                  { path: '', element: <Navigate to="categorias" replace /> },
                  { path: 'categorias', element: <CategoriesSettings /> },
                  { path: 'tags',       element: <TagsSettings /> },
                  { path: 'whatsapp',   element: <WhatsAppSettings /> },
                ],
              },
              // Rota de Painel Superadmin — acesso restrito
              {
                element: <SuperadminRoute />,
                children: [
                  {
                    path: '/admin',
                    element: <SuperadminDashboard />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
